/**
 * URL validation utilities for safe redirect handling.
 *
 * These functions prevent open redirect vulnerabilities by ensuring
 * redirect targets stay within the same origin.
 */

/**
 * Validates and sanitizes a redirect path to prevent open redirects.
 *
 * @param rawNext - The raw redirect URL/path from query params or storage
 * @param origin - The current window origin (e.g., "https://example.com")
 * @returns A safe same-origin path, or "/" if validation fails
 *
 * @example
 * // Valid same-origin paths
 * getSafeNextPath("/dashboard", "https://app.com") // => "/dashboard"
 * getSafeNextPath("/search?q=test", "https://app.com") // => "/search?q=test"
 *
 * // Invalid/malicious inputs fall back to "/"
 * getSafeNextPath("https://evil.com", "https://app.com") // => "/"
 * getSafeNextPath("//evil.com", "https://app.com") // => "/"
 * getSafeNextPath(null, "https://app.com") // => "/"
 */
export function getSafeNextPath(rawNext: string | null, origin: string): string {
  const fallback = '/';
  if (!rawNext) return fallback;

  try {
    // Parse the URL relative to the current origin
    const nextUrl = new URL(rawNext, origin);

    // Reject if the resolved URL points to a different origin
    if (nextUrl.origin !== origin) return fallback;

    const pathname = nextUrl.pathname || '/';

    // Reject protocol-relative URLs (//evil.com) which can bypass origin checks
    if (!pathname.startsWith('/') || pathname.startsWith('//')) return fallback;

    // Return the safe path with query string and hash preserved
    return `${pathname}${nextUrl.search}${nextUrl.hash}`;
  } catch {
    // URL parsing failed - return safe fallback
    return fallback;
  }
}
